import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "./Button"
import { JobCreatePayload } from "../shared-types"

/**
 * 🧠 Zod validation schema for job creation
 */
const jobSchema = z.object({
  title: z.string().min(3, "Title must be at least 3 characters"),
  description: z.string().min(10, "Description must be at least 10 characters"),
  pickup_location: z.string().min(3, "Pickup location is required"),
  delivery_location: z.string().min(3, "Delivery location is required"),
  pickup_time: z.string().min(1, "Pickup time is required"),
  delivery_time: z.string().min(1, "Delivery time is required"),
  license_class: z.string().min(1, "License class is required"),
  required_endorsements: z.array(z.string()).min(1, "At least one endorsement is required"),
  pay_rate: z.number().min(1, "Pay rate must be at least 1"),
  duration: z.number().min(1, "Duration must be at least 1 hour"),
})

/**
 * 🧾 Props expected by the job form component
 */
interface JobCreateFormProps {
  onSubmit: (data: JobCreatePayload) => Promise<void>
  isSubmitting: boolean
  error: Error | null
  className?: string
}

/**
 * 🧰 Controlled form to create a new job (dispatcher only)
 */
export function JobCreateForm({
  onSubmit,
  isSubmitting,
  error,
  className = "",
}: JobCreateFormProps) {
  const {
    handleSubmit,
    reset,
  } = useForm<JobCreatePayload>({
    resolver: zodResolver(jobSchema),
    defaultValues: {
      required_endorsements: [],
    },
  })

  const handleFormSubmit = async (data: JobCreatePayload) => {
    await onSubmit(data)
    reset()
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className={`space-y-6 ${className}`}>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
        Create New Job
      </h2>

      {/* 🛑 Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg text-red-700 dark:text-red-300">
          <p>{error.message}</p>
        </div>
      )}

      {/* 🧱 Form Inputs Placeholder */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 🔧 Inputs to be implemented next */}
      </div>

      {/* 🚀 Form Actions */}
      <div className="flex justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => reset()}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Creating..." : "Create Job"}
        </Button>
      </div>
    </form>
  )
}
